// Статистика выборки. Правила — раздел 5 контракта: всегда распределение,
// никогда одно среднее.
//
// ПОЧЕМУ СЧИТАЕТСЯ ЗДЕСЬ, А НЕ БЕРЁТСЯ С /stats. Контракт фиксирует форму
// строки прогона, но не форму ответа /stats. Считая перцентили из строк,
// приложение зависит только от того, что в контракте описано, и показывает
// в офлайне и с сервером одни и те же числа. Как только форма /stats
// попадёт в контракт, это место станет необязательным.

import type { RunRow } from './types';

export interface Percentiles {
  count: number;
  min: number;
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
  max: number;
  mean: number;
  /** Медианное абсолютное отклонение — база для порога аномалии. */
  mad: number;
  p1: number;
  p99: number;
}

/**
 * Перцентиль по методу ближайшего ранга на отсортированном массиве.
 * Линейная интерполяция здесь была бы хуже: величины целочисленные
 * (враги, патроны, комнаты), и «7.4 врага» — число, которого не бывает.
 */
export function quantileSorted(sorted: readonly number[], q: number): number {
  if (sorted.length === 0) return Number.NaN;
  const rank = Math.ceil(q * sorted.length);
  const index = Math.min(sorted.length - 1, Math.max(0, rank - 1));
  return sorted[index] as number;
}

export function describe(values: readonly number[]): Percentiles {
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  if (n === 0) {
    const nan = Number.NaN;
    return {
      count: 0, min: nan, p10: nan, p25: nan, p50: nan, p75: nan, p90: nan,
      max: nan, mean: nan, mad: nan, p1: nan, p99: nan,
    };
  }
  const p50 = quantileSorted(sorted, 0.5);
  const deviations = sorted.map((v) => Math.abs(v - p50)).sort((a, b) => a - b);
  let sum = 0;
  for (const v of sorted) sum += v;
  return {
    count: n,
    min: sorted[0] as number,
    p1: quantileSorted(sorted, 0.01),
    p10: quantileSorted(sorted, 0.1),
    p25: quantileSorted(sorted, 0.25),
    p50,
    p75: quantileSorted(sorted, 0.75),
    p90: quantileSorted(sorted, 0.9),
    p99: quantileSorted(sorted, 0.99),
    max: sorted[n - 1] as number,
    mean: sum / n,
    mad: quantileSorted(deviations, 0.5),
  };
}

/**
 * Граница между «тесной» и «широкой» величиной.
 *
 * ЭТО ЧИСЛО ИЗМЕРЕНО, А НЕ ВЫБРАНО. На тысяче прогонов из 21 величины
 * широкое распределение имеет РОВНО ОДНА — площадь пола (81 различное
 * значение). У остальных от одного до восьми: врагов 6, комнат 5, аптечек
 * 3. Ящик с усами и гистограмма с корзинами для величины на шесть
 * значений рисуют хвост, которого нет, и слипшиеся корзины; честный ответ
 * там — частотная таблица «врагов 6 → столько-то прогонов».
 */
export const TIGHT_DISTINCT_MAX = 20;

export function distinctCount(values: readonly number[]): number {
  return new Set(values).size;
}

export function isTight(values: readonly number[]): boolean {
  return distinctCount(values) <= TIGHT_DISTINCT_MAX;
}

export interface FrequencyRow {
  value: number;
  count: number;
  share: number;
  /** Накопленная доля снизу вверх: видно, где лежит медиана. */
  cumulative: number;
}

/**
 * Частотная таблица: одно целое значение — одна строка. Для тесных
 * величин это единственное честное представление распределения.
 */
export function frequencies(values: readonly number[]): FrequencyRow[] {
  const counts = new Map<number, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  const total = Math.max(1, values.length);
  let running = 0;
  return [...counts.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([value, count]) => {
      running += count;
      return { value, count, share: count / total, cumulative: running / total };
    });
}

/** Гистограмма по целочисленной величине: одно значение — один столбик. */
export interface HistogramBin {
  value: number;
  count: number;
}

export function histogram(values: readonly number[], maxBins = 40): HistogramBin[] {
  if (values.length === 0) return [];
  const counts = new Map<number, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  if (counts.size <= maxBins) {
    return [...counts.entries()]
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => a.value - b.value);
  }
  // Слишком много различных значений (площадь пола) — группируем шагом.
  const keys = [...counts.keys()].sort((a, b) => a - b);
  const lo = keys[0] as number;
  const hi = keys[keys.length - 1] as number;
  const step = Math.max(1, Math.ceil((hi - lo + 1) / maxBins));
  const binned = new Map<number, number>();
  for (const [value, count] of counts) {
    const bucket = lo + Math.floor((value - lo) / step) * step;
    binned.set(bucket, (binned.get(bucket) ?? 0) + count);
  }
  return [...binned.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => a.value - b.value);
}

// --- измеримые величины прогона --------------------------------------------

export interface Metric {
  key: string;
  label: string;
  unit: string;
  pick: (row: RunRow) => number;
  /** true — рост величины делает игру легче. Нужно для формулировок. */
  higherIsEasier: boolean;
}

export const METRICS: readonly Metric[] = [
  { key: 'enemies', label: 'Врагов', unit: 'шт', pick: (r) => r.population.enemies, higherIsEasier: false },
  { key: 'ammoTotal', label: 'Патронов на уровне', unit: 'шт', pick: (r) => r.population.ammoTotal, higherIsEasier: true },
  { key: 'ammoMargin', label: 'Запас патронов', unit: 'шт', pick: (r) => r.economy.ammoMargin, higherIsEasier: true },
  { key: 'ammoSpent', label: 'Патронов нужно жадному', unit: 'шт', pick: (r) => r.economy.ammoSpent, higherIsEasier: false },
  { key: 'medkits', label: 'Аптечек', unit: 'шт', pick: (r) => r.population.medkits, higherIsEasier: true },
  { key: 'rooms', label: 'Комнат', unit: 'шт', pick: (r) => r.layout.rooms, higherIsEasier: false },
  { key: 'doors', label: 'Дверей', unit: 'шт', pick: (r) => r.layout.doors, higherIsEasier: false },
  { key: 'floorCells', label: 'Площадь пола', unit: 'клеток', pick: (r) => r.layout.floorCells, higherIsEasier: false },
  { key: 'lootDrops', label: 'Находок', unit: 'шт', pick: (r) => r.population.lootDrops, higherIsEasier: true },
  { key: 'attempts', label: 'Попыток планировки', unit: 'шт', pick: (r) => r.layout.attempts, higherIsEasier: false },
];

export const METRIC_BY_KEY = new Map(METRICS.map((m) => [m.key, m]));

// --- сводка по датасету -----------------------------------------------------

export interface DatasetSummary {
  rows: number;
  configHash: string | null;
  configHashes: string[];
  /** Доля прогонов, у которых есть хотя бы один флаг или нарушен дефицит. */
  brokenShare: number;
  brokenSeeds: number[];
  ammoSurplusShare: number;
  layoutFallbackShare: number;
  noEnemiesShare: number;
  cellCollisionShare: number;
  relaxedClearanceShare: number;
  flagCounts: Array<{ flag: string; count: number }>;
  metrics: Map<string, Percentiles>;
}

/** Прогон считается сломанным, если у него есть флаг или нарушен дефицит. */
export function isBroken(row: RunRow): boolean {
  return row.flags.length > 0 || !row.economy.respectsAmmoDeficit;
}

export function summarize(rows: readonly RunRow[]): DatasetSummary {
  const hashes = [...new Set(rows.map((r) => r.configHash))];
  const flagCounts = new Map<string, number>();
  let broken = 0;
  let surplus = 0;
  let fallback = 0;
  let noEnemies = 0;
  let collisions = 0;
  let relaxed = 0;
  const brokenSeeds: number[] = [];

  for (const row of rows) {
    for (const flag of row.flags) flagCounts.set(flag, (flagCounts.get(flag) ?? 0) + 1);
    if (isBroken(row)) {
      broken++;
      brokenSeeds.push(row.seed);
    }
    if (!row.economy.respectsAmmoDeficit) surplus++;
    if (row.layout.usedFallback) fallback++;
    if (row.population.enemies === 0) noEnemies++;
    if (row.diagnostics.cellCollisions > 0) collisions++;
    if (row.diagnostics.relaxedEnemyClearance) relaxed++;
  }

  const metrics = new Map<string, Percentiles>();
  for (const metric of METRICS) {
    metrics.set(metric.key, describe(rows.map(metric.pick)));
  }

  const n = Math.max(1, rows.length);
  return {
    rows: rows.length,
    configHash: hashes.length === 1 ? (hashes[0] as string) : null,
    configHashes: hashes,
    brokenShare: broken / n,
    brokenSeeds,
    ammoSurplusShare: surplus / n,
    layoutFallbackShare: fallback / n,
    noEnemiesShare: noEnemies / n,
    cellCollisionShare: collisions / n,
    relaxedClearanceShare: relaxed / n,
    flagCounts: [...flagCounts.entries()]
      .map(([flag, count]) => ({ flag, count }))
      .sort((a, b) => b.count - a.count),
    metrics,
  };
}

// --- аномалии ---------------------------------------------------------------
//
// ЧЕТЫРЕ РАЗНЫХ ВОПРОСА К ДАННЫМ, А НЕ ОДНА ТАБЛИЦА. Свалить их в кучу —
// значит потерять самый важный: «здесь сломано» тонет среди «здесь редко».
//
//   broken    физически сломанный прогон: флаг симуляции, нарушенный
//             дефицит, налезшие комнаты. Это находки, а не выбросы.
//   rare      редкое событие: запасная планировка, ослабленный отступ.
//             Само по себе допустимо, но доля должна быть мала.
//   frequency хвост частотной таблицы ТЕСНОЙ величины: значение, которое
//             встретилось меньше чем в 1% прогонов. Для величины на шесть
//             значений это единственный осмысленный «выброс» — 3×MAD там
//             не работает, MAD равен нулю.
//   outlier   настоящий выброс ШИРОКОЙ величины: 3×MAD от медианы либо
//             ниже p1 / выше p99. Уместен ровно там, где значений много.

export type AnomalyKind = 'broken' | 'rare' | 'frequency' | 'outlier';

export const ANOMALY_KIND_TITLES: Record<AnomalyKind, string> = {
  broken: 'Сломанные прогоны',
  rare: 'Редкие события',
  frequency: 'Хвосты частотных таблиц',
  outlier: 'Выбросы широких величин',
};

export const ANOMALY_KIND_NOTES: Record<AnomalyKind, string> = {
  broken:
    'Флаг симуляции, нарушенный дефицит патронов или налезшие комнаты. Это находки, ' +
    'а не статистика: их ставит сам spusk-sim, и спорить с ним перцентилям не о чем.',
  rare:
    'События, которые допустимы, но должны быть редкими: запасная планировка, ослабленный ' +
    'отступ от входа, обрезка патронов правилом дефицита. Смотреть надо на долю, а не на список.',
  frequency:
    'Величины вроде числа врагов принимают шесть-семь значений на тысячу прогонов. ' +
    'Правило 3×MAD там молчит (MAD равен нулю), поэтому «редким» считается значение, ' +
    'встретившееся меньше чем в 1% прогонов: «врагов 11 — дважды на тысячу».',
  outlier:
    'Отклонение больше 3×MAD от медианы либо попадание ниже p1 или выше p99. ' +
    'Применяется только к величинам с широким распределением — на этих данных это площадь пола.',
};

/** Доля, ниже которой значение тесной величины считается редким. */
export const FREQUENCY_TAIL_SHARE = 0.01;
const MAD_THRESHOLD = 3;
/** Множитель перевода MAD в оценку сигмы у нормального распределения. */
const MAD_TO_SIGMA = 1.4826;

export interface AnomalyReason {
  metric: string;
  label: string;
  value: number;
  median: number;
  /** Отклонение в единицах MAD. Ноль, когда правило не применялось. */
  madUnits: number;
  percent: number;
  rule: 'mad' | 'tail' | 'frequency' | 'flag' | 'boolean';
  /** Фраза по-русски про одну причину. */
  phrase: string;
}

export interface Anomaly {
  seed: number;
  kind: AnomalyKind;
  row: RunRow;
  reasons: AnomalyReason[];
  /** Готовая фраза по-русски: раздел 5 контракта требует именно её. */
  sentence: string;
  severity: number;
}

export const FLAG_PHRASES: Record<string, string> = {
  layout_fallback: 'планировка собрана запасным путём',
  ammo_surplus: 'патронов избыток — дефицит сломан',
  no_enemies: 'на уровне нет ни одного врага',
  no_ammo: 'на уровне нет ни одного патрона',
  cell_collisions: 'комнаты налезли друг на друга',
  drops_break_deficit:
    'без патронов с трупов игрок был беднее, чем ему хочется, а с ними стал богаче',
};

/** Редкие булевы признаки: допустимы, но доля должна быть мала. */
const RARE_FLAGS: Array<{ key: string; pick: (row: RunRow) => boolean; phrase: string }> = [
  {
    key: 'usedFallback',
    pick: (r) => r.layout.usedFallback,
    phrase: 'планировка собрана запасным путём',
  },
  {
    key: 'relaxedEnemyClearance',
    pick: (r) => r.diagnostics.relaxedEnemyClearance,
    phrase: 'отступ от входа пришлось ослабить',
  },
  {
    key: 'ammoClippedByDeficitRule',
    pick: (r) => r.diagnostics.ammoClippedByDeficitRule,
    phrase: 'патроны обрезаны правилом дефицита',
  },
];

export interface AnomalyReport {
  broken: Anomaly[];
  rare: Anomaly[];
  frequency: Anomaly[];
  outlier: Anomaly[];
  /** Какие величины признаны широкими: на них работает правило 3×MAD. */
  wideMetrics: string[];
  tightMetrics: string[];
  total: number;
}

export function emptyAnomalyReport(): AnomalyReport {
  return { broken: [], rare: [], frequency: [], outlier: [], wideMetrics: [], tightMetrics: [], total: 0 };
}

/**
 * Аномалии по правилам раздела 5 контракта, разложенные по четырём видам.
 *
 * Разделение не косметическое: «сломан дефицит» и «врагов 11 вместо 6» —
 * разные новости, и объединять их в один список значит прятать первую
 * за второй.
 */
export function findAnomalies(rows: readonly RunRow[], summary: DatasetSummary): AnomalyReport {
  const report = emptyAnomalyReport();
  if (rows.length === 0) return report;

  // Тесная величина или широкая — решают сами данные, а не список имён:
  // тот же конфиг с другой сеткой может сделать комнаты широкой величиной.
  const values = new Map<string, number[]>();
  const freq = new Map<string, Map<number, number>>();
  for (const metric of METRICS) {
    const column = rows.map(metric.pick);
    values.set(metric.key, column);
    const counts = new Map<number, number>();
    for (const v of column) counts.set(v, (counts.get(v) ?? 0) + 1);
    freq.set(metric.key, counts);
    if (isTight(column)) report.tightMetrics.push(metric.key);
    else report.wideMetrics.push(metric.key);
  }

  for (const row of rows) {
    // 1. Сломанное. Проверяется первым: если прогон сломан, остальные
    //    вопросы к нему второстепенны, и он не должен попасть в две таблицы.
    const brokenReasons: AnomalyReason[] = [];
    if (!row.economy.respectsAmmoDeficit) {
      brokenReasons.push({
        metric: 'ammoMargin', label: 'Запас патронов', value: row.economy.ammoMargin,
        median: summary.metrics.get('ammoMargin')?.p50 ?? 0, madUnits: 0, percent: 0, rule: 'boolean',
        phrase: `патронов больше, чем нужно жадному (запас ${row.economy.ammoMargin >= 0 ? '+' : ''}${row.economy.ammoMargin})`,
      });
    }
    for (const flag of row.flags) {
      brokenReasons.push({
        metric: 'flag', label: flag, value: 1, median: 0, madUnits: 0, percent: 0, rule: 'flag',
        phrase: FLAG_PHRASES[flag] ?? `флаг ${flag}`,
      });
    }
    if (row.diagnostics.cellCollisions > 0) {
      brokenReasons.push({
        metric: 'cellCollisions', label: 'Налезшие клетки', value: row.diagnostics.cellCollisions,
        median: 0, madUnits: 0, percent: 0, rule: 'boolean',
        phrase: `комнаты налезли друг на друга в ${row.diagnostics.cellCollisions} клетке(ах)`,
      });
    }
    if (brokenReasons.length > 0) {
      report.broken.push({
        seed: row.seed, kind: 'broken', row, reasons: brokenReasons,
        sentence: `seed ${row.seed}: ${brokenReasons.map((r) => r.phrase).join(', ')}.`,
        severity: 1000 + brokenReasons.length,
      });
      continue;
    }

    // 2. Редкие события.
    const rareReasons: AnomalyReason[] = [];
    for (const rare of RARE_FLAGS) {
      if (!rare.pick(row)) continue;
      rareReasons.push({
        metric: rare.key, label: rare.phrase, value: 1, median: 0, madUnits: 0, percent: 0,
        rule: 'boolean', phrase: rare.phrase,
      });
    }
    if (rareReasons.length > 0) {
      report.rare.push({
        seed: row.seed, kind: 'rare', row, reasons: rareReasons,
        sentence: `seed ${row.seed}: ${rareReasons.map((r) => r.phrase).join(', ')}.`,
        severity: 100 + rareReasons.length,
      });
      continue;
    }

    // 3. Хвост частотной таблицы у тесных величин.
    const freqReasons: AnomalyReason[] = [];
    for (const key of report.tightMetrics) {
      const metric = METRIC_BY_KEY.get(key);
      const counts = freq.get(key);
      if (!metric || !counts) continue;
      const value = metric.pick(row);
      const count = counts.get(value) ?? 0;
      const share = count / rows.length;
      if (share >= FREQUENCY_TAIL_SHARE) continue;
      const median = summary.metrics.get(key)?.p50 ?? 0;
      freqReasons.push({
        metric: key, label: metric.label, value, median, madUnits: 0,
        percent: median !== 0 ? ((value - median) / Math.abs(median)) * 100 : 0,
        rule: 'frequency',
        phrase:
          `${metric.label.toLowerCase()} ${value} при медиане ${median} — ` +
          `так вышло ${count} раз(а) на ${rows.length}`,
      });
    }

    // 4. Настоящий выброс широкой величины.
    const outlierReasons: AnomalyReason[] = [];
    for (const key of report.wideMetrics) {
      const metric = METRIC_BY_KEY.get(key);
      const stat = summary.metrics.get(key);
      if (!metric || !stat || stat.count === 0) continue;
      const value = metric.pick(row);
      const spread = stat.mad * MAD_TO_SIGMA;
      const madUnits = spread > 0 ? Math.abs(value - stat.p50) / spread : 0;
      const byMad = spread > 0 && madUnits > MAD_THRESHOLD;
      const byTail = value < stat.p1 || value > stat.p99;
      if (!byMad && !byTail) continue;
      const percent = stat.p50 !== 0 ? ((value - stat.p50) / Math.abs(stat.p50)) * 100 : 0;
      outlierReasons.push({
        metric: key, label: metric.label, value, median: stat.p50,
        madUnits: byMad ? madUnits : 0, percent, rule: byMad ? 'mad' : 'tail',
        phrase:
          `${metric.label.toLowerCase()} ${value > stat.p50 ? 'больше' : 'меньше'} медианы на ` +
          `${Math.round(Math.abs(percent))}% (${value} против ${stat.p50})`,
      });
    }

    if (outlierReasons.length > 0) {
      report.outlier.push({
        seed: row.seed, kind: 'outlier', row, reasons: outlierReasons,
        sentence: `seed ${row.seed}: ${outlierReasons.map((r) => r.phrase).join(', ')}.`,
        severity: outlierReasons.reduce((max, r) => Math.max(max, r.madUnits || 1), 0),
      });
    } else if (freqReasons.length > 0) {
      report.frequency.push({
        seed: row.seed, kind: 'frequency', row, reasons: freqReasons,
        sentence: `seed ${row.seed}: ${freqReasons.map((r) => r.phrase).join(', ')}.`,
        severity: freqReasons.length,
      });
    }
  }

  for (const list of [report.broken, report.rare, report.frequency, report.outlier]) {
    list.sort((a, b) => b.severity - a.severity || a.seed - b.seed);
  }
  report.total =
    report.broken.length + report.rare.length + report.frequency.length + report.outlier.length;
  return report;
}

// --- парное сравнение -------------------------------------------------------

export interface FlipDirection {
  metric: string;
  label: string;
  before: number;
  after: number;
  delta: number;
}

export interface SeedFlip {
  seed: number;
  /** Что перевернулось: соблюдение дефицита, наличие врагов, флаги. */
  kind: 'deficit' | 'flags' | 'no_enemies' | 'fallback';
  before: string;
  after: string;
  sentence: string;
  changes: FlipDirection[];
}

export interface SeedShift {
  seed: number;
  before: number;
  after: number;
  delta: number;
}

export interface Comparison {
  sharedSeeds: number;
  onlyInA: number;
  onlyInB: number;
  hashA: string | null;
  hashB: string | null;
  /** true — конфиги одинаковые: сравнивать нечего, и это надо сказать. */
  sameConfig: boolean;
  flips: SeedFlip[];
  /**
   * Сдвиг по величине для каждого seed, отсортированный по величине сдвига.
   * Нужен там, где качественно не перевернулось НИЧЕГО: правка всё равно
   * что-то сделала, и человеку надо увидеть, на каких именно seed.
   */
  shiftsFor: (metricKey: string) => SeedShift[];
  /** Сдвиг медиан. Второстепенно, но нужно для контекста. */
  medianShift: Array<{ metric: string; label: string; a: number; b: number; delta: number }>;
}

function stateOf(row: RunRow) {
  return {
    deficit: row.economy.respectsAmmoDeficit,
    flags: [...row.flags].sort().join(','),
    noEnemies: row.population.enemies === 0,
    fallback: row.layout.usedFallback,
  };
}

/**
 * Сравнение двух выборок по ОДНИМ И ТЕМ ЖЕ seed. Главный выход —
 * список перевернувшихся seed, а не сдвиг средних: среднее скрывает
 * ровно те десять прогонов, ради которых правку и делали.
 */
export function compare(a: readonly RunRow[], b: readonly RunRow[]): Comparison {
  const byA = new Map(a.map((r) => [r.seed, r]));
  const byB = new Map(b.map((r) => [r.seed, r]));
  const shared: number[] = [];
  for (const seed of byA.keys()) if (byB.has(seed)) shared.push(seed);
  shared.sort((x, y) => x - y);

  const flips: SeedFlip[] = [];
  for (const seed of shared) {
    const rowA = byA.get(seed) as RunRow;
    const rowB = byB.get(seed) as RunRow;
    const sa = stateOf(rowA);
    const sb = stateOf(rowB);

    const changes: FlipDirection[] = [];
    for (const metric of METRICS) {
      const before = metric.pick(rowA);
      const after = metric.pick(rowB);
      if (before !== after) {
        changes.push({ metric: metric.key, label: metric.label, before, after, delta: after - before });
      }
    }

    if (sa.deficit !== sb.deficit) {
      flips.push({
        seed,
        kind: 'deficit',
        before: sa.deficit ? 'дефицит соблюдён' : 'избыток патронов',
        after: sb.deficit ? 'дефицит соблюдён' : 'избыток патронов',
        sentence: sb.deficit
          ? `seed ${seed}: избыток патронов исчез — дефицит восстановлен.`
          : `seed ${seed}: дефицит сломался, патронов стало больше, чем нужно жадному.`,
        changes,
      });
      continue;
    }
    if (sa.noEnemies !== sb.noEnemies) {
      flips.push({
        seed,
        kind: 'no_enemies',
        before: sa.noEnemies ? 'врагов нет' : `врагов ${rowA.population.enemies}`,
        after: sb.noEnemies ? 'врагов нет' : `врагов ${rowB.population.enemies}`,
        sentence: sb.noEnemies
          ? `seed ${seed}: уровень остался без единого врага.`
          : `seed ${seed}: пустой уровень наконец получил врагов (${rowB.population.enemies}).`,
        changes,
      });
      continue;
    }
    if (sa.fallback !== sb.fallback) {
      flips.push({
        seed,
        kind: 'fallback',
        before: sa.fallback ? 'запасная планировка' : 'обычная планировка',
        after: sb.fallback ? 'запасная планировка' : 'обычная планировка',
        sentence: sb.fallback
          ? `seed ${seed}: планировка перестала собираться и ушла на запасной путь.`
          : `seed ${seed}: планировка перестала нуждаться в запасном пути.`,
        changes,
      });
      continue;
    }
    if (sa.flags !== sb.flags) {
      flips.push({
        seed,
        kind: 'flags',
        before: sa.flags || '—',
        after: sb.flags || '—',
        sentence: `seed ${seed}: набор флагов изменился с «${sa.flags || 'чисто'}» на «${sb.flags || 'чисто'}».`,
        changes,
      });
    }
  }

  const sharedA = shared.map((s) => byA.get(s) as RunRow);
  const sharedB = shared.map((s) => byB.get(s) as RunRow);
  const medianShift = METRICS.map((metric) => {
    const av = describe(sharedA.map(metric.pick)).p50;
    const bv = describe(sharedB.map(metric.pick)).p50;
    return { metric: metric.key, label: metric.label, a: av, b: bv, delta: bv - av };
  });

  const hashA = [...new Set(a.map((r) => r.configHash))];
  const hashB = [...new Set(b.map((r) => r.configHash))];

  return {
    sharedSeeds: shared.length,
    shiftsFor: (metricKey: string): SeedShift[] => {
      const metric = METRIC_BY_KEY.get(metricKey);
      if (!metric) return [];
      const shifts: SeedShift[] = [];
      for (const seed of shared) {
        const before = metric.pick(byA.get(seed) as RunRow);
        const after = metric.pick(byB.get(seed) as RunRow);
        if (before !== after) shifts.push({ seed, before, after, delta: after - before });
      }
      shifts.sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta) || x.seed - y.seed);
      return shifts;
    },
    onlyInA: byA.size - shared.length,
    onlyInB: byB.size - shared.length,
    hashA: hashA.length === 1 ? (hashA[0] as string) : null,
    hashB: hashB.length === 1 ? (hashB[0] as string) : null,
    sameConfig: hashA.length === 1 && hashB.length === 1 && hashA[0] === hashB[0],
    flips,
    medianShift,
  };
}
